import { Extension } from "@codemirror/state";
import {
  Decoration,
  DecorationSet,
  EditorView,
  ViewPlugin,
  ViewUpdate
} from "@codemirror/view";
import { RangeSetBuilder } from "@codemirror/state";
import { StateField, StateEffect } from "@codemirror/state";

// State field to track the error line
const setErrorLine = StateEffect.define<number | null>();

const errorLineField = StateField.define<number | null>({
  create() {
    return null;
  },
  update(value, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setErrorLine)) {
        return effect.value;
      }
    }
    return value;
  }
});

// Decoration for error line highlighting - using line decoration for full line coverage
const errorLineDecoration = Decoration.line({
  class: "cm-error-line",
  attributes: { "data-error-line": "true" }
});

// Plugin to highlight error lines
const errorLineHighlightPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
      this.decorations = this.buildDecorations(view);
    }

    update(update: ViewUpdate) {
      // Update if document changes or error line changes
      if (
        update.docChanged ||
        update.viewportChanged ||
        update.transactions.some(tr => tr.effects.some(e => e.is(setErrorLine)))
      ) {
        this.decorations = this.buildDecorations(update.view);
      }
    }

    buildDecorations(view: EditorView): DecorationSet {
      const builder = new RangeSetBuilder<Decoration>();
      const errorLine = view.state.field(errorLineField);

      if (errorLine !== null && errorLine > 0) {
        const doc = view.state.doc;

        // Check if the error line exists in the document
        if (errorLine <= doc.lines) {
          const lineStart = doc.line(errorLine).from;

          // Add line decoration for the entire line (including whitespace and line ending)
          builder.add(lineStart, lineStart, errorLineDecoration);
        }
      }

      return builder.finish();
    }
  },
  {
    decorations: v => v.decorations
  }
);

// Theme for error line highlighting
const errorLineTheme = EditorView.theme({
  ".cm-error-line": {
    backgroundColor: "rgba(255, 0, 0, 0.2)",
    borderLeft: "3px solid #ff0000"
  }
});

// Create the extension factory function
export function createErrorLineHighlighter(
  errorLine: number | null
): Extension {
  return [
    errorLineField.init(() => errorLine),
    errorLineHighlightPlugin,
    errorLineTheme
  ];
}

// Function to update error line
export function updateErrorLine(view: EditorView, line: number | null) {
  view.dispatch({
    effects: setErrorLine.of(line)
  });
}
